import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import type { BillStage } from '@/lib/stage-utils';
import { useToast } from '@/hooks/use-toast';
import { ConfidenceRouter } from './processing/ConfidenceRouter';
import { PriorityRulesManager } from '@/components/manual-review/PriorityRulesManager';
import { IntelligentAssignmentEngine } from '@/components/manual-review/IntelligentAssignmentEngine';
import { TeamWorkloadView } from '@/components/manual-review/TeamWorkloadView';
import { QualityControlDashboard } from '@/components/manual-review/QualityControlDashboard';
import { RulesEngineBuilder } from '@/components/validation/RulesEngineBuilder';
import { BaselineConfiguration } from '@/components/validation/BaselineConfiguration';
import { AnomalyDetectionDashboard } from '@/components/validation/AnomalyDetectionDashboard';
import { ApprovalPolicyManager } from '@/components/approval/ApprovalPolicyManager';
import { DelegationManager } from '@/components/approval/DelegationManager';
import { ApprovalAnalyticsDashboard } from '@/components/approval/ApprovalAnalyticsDashboard';
import { ExportTemplateManager } from '@/components/payment/ExportTemplateManager';
import { PaymentScheduleManager } from '@/components/payment/PaymentScheduleManager';
import { UpcomingPaymentsCalendar } from '@/components/payment/UpcomingPaymentsCalendar';
import { AccountingIntegrationSettings } from '@/components/payment/AccountingIntegrationSettings';
import { RetentionPolicyManager } from '@/components/archive/RetentionPolicyManager';
import { ArchiveAnalyticsDashboard } from '@/components/archive/ArchiveAnalyticsDashboard';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { supabase } from '@/integrations/supabase/client';

interface StageRulesSettingsProps {
  stage: BillStage;
}

export function StageRulesSettings({ stage }: StageRulesSettingsProps) {
  const { toast } = useToast();
  const { currentWorkspace } = useWorkspace();
  const [autoRouting, setAutoRouting] = useState(true);
  const [slaHours, setSlaHours] = useState<number>(stage === 'intake' ? 12 : stage === 'validation' || stage === 'approval' ? 12 : 24);
  const [confidenceThreshold, setConfidenceThreshold] = useState<number>(80);
  const [requireMandatoryFields, setRequireMandatoryFields] = useState(true);
  const [userId, setUserId] = useState<string>('');

  // Get current user ID
  useState(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      setUserId(user?.id || '');
    });
  });

  // Render stage-specific settings
  const renderStageSettings = () => {
    switch (stage) {
      case 'processing':
        return <ConfidenceRouter />;
        
      case 'manual_review':
        return (
          <Tabs defaultValue="priority">
            <TabsList className="w-full">
              <TabsTrigger value="priority">Priority Rules</TabsTrigger>
              <TabsTrigger value="assignment">Assignment</TabsTrigger>
              <TabsTrigger value="quality">Quality Control</TabsTrigger>
            </TabsList>
            <TabsContent value="priority" className="space-y-4">
              <PriorityRulesManager workspaceId={currentWorkspace?.id || ''} />
            </TabsContent>
            <TabsContent value="assignment" className="space-y-4">
              <IntelligentAssignmentEngine workspaceId={currentWorkspace?.id || ''} />
              <TeamWorkloadView />
            </TabsContent>
            <TabsContent value="quality" className="space-y-4">
              <QualityControlDashboard />
            </TabsContent>
          </Tabs>
        );
        
      case 'validation':
        return (
          <Tabs defaultValue="rules">
            <TabsList className="w-full">
              <TabsTrigger value="rules">Validation Rules</TabsTrigger>
              <TabsTrigger value="anomalies">Anomaly Detection</TabsTrigger>
            </TabsList>
            <TabsContent value="rules" className="space-y-4">
              <RulesEngineBuilder workspaceId={currentWorkspace?.id || ''} />
            </TabsContent>
            <TabsContent value="anomalies" className="space-y-4">
              <BaselineConfiguration />
              <AnomalyDetectionDashboard workspaceId={currentWorkspace?.id || ''} />
            </TabsContent>
          </Tabs>
        );
        
      case 'approval':
        return (
          <Tabs defaultValue="policies">
            <TabsList className="w-full">
              <TabsTrigger value="policies">Approval Policies</TabsTrigger>
              <TabsTrigger value="delegation">Delegation</TabsTrigger>
              <TabsTrigger value="analytics">Analytics</TabsTrigger>
            </TabsList>
            <TabsContent value="policies" className="space-y-4">
              <ApprovalPolicyManager workspaceId={currentWorkspace?.id || ''} />
            </TabsContent>
            <TabsContent value="delegation" className="space-y-4">
              <DelegationManager userId={userId} />
            </TabsContent>
            <TabsContent value="analytics" className="space-y-4">
              <ApprovalAnalyticsDashboard />
            </TabsContent>
          </Tabs>
        );
        
      case 'payment_ready':
        return (
          <Tabs defaultValue="export">
            <TabsList className="w-full">
              <TabsTrigger value="export">Export Templates</TabsTrigger>
              <TabsTrigger value="schedule">Payment Schedules</TabsTrigger>
              <TabsTrigger value="integration">Integrations</TabsTrigger>
            </TabsList>
            <TabsContent value="export" className="space-y-4">
              <ExportTemplateManager workspaceId={currentWorkspace?.id || ''} />
            </TabsContent>
            <TabsContent value="schedule" className="space-y-4">
              <PaymentScheduleManager workspaceId={currentWorkspace?.id || ''} />
              <UpcomingPaymentsCalendar workspaceId={currentWorkspace?.id || ''} />
            </TabsContent>
            <TabsContent value="integration" className="space-y-4">
              <AccountingIntegrationSettings />
            </TabsContent>
          </Tabs>
        );
        
      case 'archive':
        return (
          <Tabs defaultValue="retention">
            <TabsList className="w-full">
              <TabsTrigger value="retention">Retention Policies</TabsTrigger>
              <TabsTrigger value="analytics">Analytics</TabsTrigger>
            </TabsList>
            <TabsContent value="retention" className="space-y-4">
              <RetentionPolicyManager workspaceId={currentWorkspace?.id || ''} />
            </TabsContent>
            <TabsContent value="analytics" className="space-y-4">
              <ArchiveAnalyticsDashboard workspaceId={currentWorkspace?.id} />
            </TabsContent>
          </Tabs>
        );
        
      default:
        // Generic stage rules for intake/extraction
        return renderGenericRules();
    }
  };

  const renderGenericRules = () => (
    <div className="grid gap-4 md:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle>Automation</CardTitle>
          <CardDescription>Auto-move and routing rules for this stage.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <Label htmlFor="auto-routing">Enable auto-routing</Label>
            <Switch id="auto-routing" checked={autoRouting} onCheckedChange={setAutoRouting} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="sla">SLA duration (hours)</Label>
            <Input id="sla" type="number" value={slaHours} onChange={(e) => setSlaHours(parseInt(e.target.value || '0', 10))} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Data Quality</CardTitle>
          <CardDescription>Thresholds and mandatory checks.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <Label htmlFor="mandatory">Require mandatory fields</Label>
            <Switch id="mandatory" checked={requireMandatoryFields} onCheckedChange={setRequireMandatoryFields} />
          </div>
          <div className="space-y-2">
            <Label>Confidence threshold</Label>
            <Slider value={[confidenceThreshold]} onValueChange={(v) => setConfidenceThreshold(v[0] || 0)} max={100} step={1} />
            <div className="text-sm text-muted-foreground">{confidenceThreshold}%</div>
          </div>
        </CardContent>
      </Card>

      <div className="md:col-span-2 flex justify-end">
        <Button
          onClick={() =>
            toast({ title: 'Saved', description: 'Rules saved locally for now. (MVP)' })
          }
        >
          Save
        </Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      {renderStageSettings()}
    </div>
  );
}
